import { cn } from '@/common/lib/cn/cn'
import {
    Children,
    forwardRef,
    isValidElement,
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react'
import type React from 'react'
import { Icon, type IconName } from '../../Icon'
import { Menu, MenuItem, MenuSelectRow } from '../../Menu'
import { Portal } from '../../Portal/Portal'
import { Button } from '../Button/Button'
import type { ButtonProps } from '../Button/Button'
import './select.css'

/** Режимы триггера: обычные варианты кнопки + сокращения для иконки (`→ outline` / `→ ghost`). */
export type SelectVariant = ButtonProps['variant'] | 'iconButton' | 'iconButtonGhost'

export type SelectType = {
    children: React.ReactNode
    size?: 'sm' | 'md' | 'lg' | 'xl'
    emphasis?: ButtonProps['emphasis']
    beforeIcon?: IconName
    placeholder?: string
    portalContainer?: string | HTMLElement
    variant?: SelectVariant
    /** Пробрасывается в триггер `Button` (состояние «выбрано» для icon-only). */
    active?: boolean
    icon?: ReactNode
    menuMinWidth?: number
    showChevron?: boolean
    showText?: boolean
} & Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'>

export type SelectOptionType = {
    children: React.ReactNode
    renderContent?: React.ReactNode
    renderValue?: React.ReactNode
    leftSlot?: React.ReactNode
    rightSlot?: React.ReactNode
} & React.OptionHTMLAttributes<HTMLOptionElement>

type SelectMenuPosition = {
    top?: number
    bottom?: number
    left: number
    /** При `showText` — ширина по триггеру; при `!showText` не задаётся (список `max-content`). */
    width?: number
    /** Ширина триггера — для `minWidth` у списка при `!showText`. */
    triggerWidth: number
    maxHeight?: number
    transformOrigin?: 'top' | 'bottom'
}

const MENU_GAP = 6
/** Минимальная высота списка, если во viewport почти нет места */
const MENU_MIN_MAX_HEIGHT_PX = 48
const MENU_ANIMATION_DURATION = 150

const normalizeValue = (value: unknown) => {
    if (value === undefined || value === null) {
        return ''
    }

    return String(value)
}

export const Select = forwardRef<HTMLSelectElement, SelectType>(
    (
        {
            children,
            size = 'md',
            emphasis = 'neutral',
            beforeIcon,
            placeholder,
            portalContainer = 'body',
            className,
            disabled,
            variant = 'outline',
            icon,
            showText = true,
            showChevron = true,
            menuMinWidth,
            active,
            ...props
        },
        ref
    ) => {
        const { value, defaultValue, onChange, multiple, 'aria-label': ariaLabel, ...restProps } = props

        const buttonVariant: ButtonProps['variant'] =
            variant === 'iconButton' ? 'outline' : variant === 'iconButtonGhost' ? 'ghost' : variant
        const [isOpen, setIsOpen] = useState(false)
        const [isClosing, setIsClosing] = useState(false)
        const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
        const optionsRefs = useRef<Array<HTMLButtonElement | HTMLAnchorElement | null>>([])
        const wrapperRef = useRef<HTMLLabelElement>(null)
        const triggerRef = useRef<HTMLButtonElement | null>(null)

        const focusTrigger = () => {
            triggerRef.current?.focus()
        }
        const menuRef = useRef<HTMLUListElement>(null)
        const menuInitialFocusDoneRef = useRef(false)
        const nativeSelectRef = useRef<HTMLSelectElement | null>(
            null
        ) as React.MutableRefObject<HTMLSelectElement | null>
        const listboxId = useId()
        const [menuPosition, setMenuPosition] = useState<SelectMenuPosition | null>(null)

        const updateMenuPosition = useCallback(() => {
            if (!triggerRef.current) {
                return
            }

            const rect = triggerRef.current.getBoundingClientRect()
            const viewportHeight = window.innerHeight
            const viewportWidth = window.innerWidth

            const availableBelow = Math.max(0, Math.floor(viewportHeight - rect.bottom - MENU_GAP))
            const availableAbove = Math.max(0, Math.floor(rect.top - MENU_GAP))
            const showAbove = availableAbove > availableBelow

            const rawMax = showAbove ? availableAbove : availableBelow
            const maxHeightPx = Math.max(Math.floor(rawMax), MENU_MIN_MAX_HEIGHT_PX)

            const minMenu = menuMinWidth ?? 240
            const provisionalWidth = showText ? rect.width : Math.max(rect.width, minMenu)

            let left = rect.left
            if (left + provisionalWidth > viewportWidth) {
                left = Math.max(MENU_GAP, viewportWidth - provisionalWidth - MENU_GAP)
            }
            if (left < MENU_GAP) {
                left = MENU_GAP
            }

            setMenuPosition({
                ...(showAbove ? { bottom: viewportHeight - rect.top + MENU_GAP } : { top: rect.bottom + MENU_GAP }),
                left,
                triggerWidth: rect.width,
                ...(showText ? { width: rect.width } : {}),
                maxHeight: maxHeightPx,
                transformOrigin: showAbove ? 'bottom' : 'top',
            })
        }, [menuMinWidth, showText])

        const handleClose = useCallback(() => {
            if (closeTimeoutRef.current) {
                return
            }
            setIsClosing(true)
            closeTimeoutRef.current = setTimeout(() => {
                setIsOpen(false)
                setIsClosing(false)
                setMenuPosition(null)
                closeTimeoutRef.current = null
            }, MENU_ANIMATION_DURATION)
        }, [])

        useEffect(() => {
            return () => {
                if (closeTimeoutRef.current) {
                    clearTimeout(closeTimeoutRef.current)
                }
            }
        }, [])

        const options = useMemo(() => {
            return Children.toArray(children).reduce<Array<SelectOptionType>>((acc, child) => {
                if (
                    isValidElement<SelectOptionType>(child) &&
                    (child.type === SelectOption || child.type === 'option')
                ) {
                    acc.push(child.props)
                }

                return acc
            }, [])
        }, [children])

        const initialValue = useMemo(() => {
            if (value !== undefined) {
                return normalizeValue(value)
            }

            if (defaultValue !== undefined) {
                return normalizeValue(defaultValue)
            }

            return ''
        }, [defaultValue, value])

        const [internalValue, setInternalValue] = useState(initialValue)
        const isControlled = value !== undefined
        const currentValue = isControlled ? normalizeValue(value) : internalValue

        useEffect(() => {
            if (isControlled || !nativeSelectRef.current) {
                return
            }

            const nativeValue = normalizeValue(nativeSelectRef.current.value)
            if (nativeValue === internalValue) {
                return
            }

            setInternalValue(nativeValue)
        }, [isControlled, internalValue, options, disabled])

        const selectedOption = options.find(option => normalizeValue(option.value) === currentValue)
        const selectedIndex = options.findIndex(option => normalizeValue(option.value) === currentValue)
        const [highlightedIndex, setHighlightedIndex] = useState(selectedIndex > -1 ? selectedIndex : 0)

        useEffect(() => {
            if (selectedIndex < 0) {
                return
            }

            setHighlightedIndex(selectedIndex)
        }, [selectedIndex])

        useEffect(() => {
            if (!isOpen) {
                return
            }

            const handleClickOutside = (event: MouseEvent) => {
                const target = event.target as Node
                const isInsideTrigger = wrapperRef.current?.contains(target)
                const isInsideMenu = menuRef.current?.contains(target)

                if (!isInsideTrigger && !isInsideMenu) {
                    handleClose()
                }
            }

            const handleEscape = (event: KeyboardEvent) => {
                if (event.key !== 'Escape') {
                    return
                }

                handleClose()
                focusTrigger()
            }

            document.addEventListener('mousedown', handleClickOutside)
            document.addEventListener('keydown', handleEscape)

            return () => {
                document.removeEventListener('mousedown', handleClickOutside)
                document.removeEventListener('keydown', handleEscape)
            }
        }, [isOpen, handleClose])

        useLayoutEffect(() => {
            if (!isOpen || disabled) {
                return
            }

            updateMenuPosition()
        }, [isOpen, disabled, updateMenuPosition, options.length])

        useEffect(() => {
            if (!isOpen) {
                return
            }

            const handleReposition = () => {
                updateMenuPosition()
            }

            window.addEventListener('resize', handleReposition)
            window.addEventListener('scroll', handleReposition, true)
            const vv = window.visualViewport
            vv?.addEventListener('resize', handleReposition)
            vv?.addEventListener('scroll', handleReposition)

            return () => {
                window.removeEventListener('resize', handleReposition)
                window.removeEventListener('scroll', handleReposition, true)
                vv?.removeEventListener('resize', handleReposition)
                vv?.removeEventListener('scroll', handleReposition)
            }
        }, [isOpen, updateMenuPosition])

        useLayoutEffect(() => {
            if (!isOpen || showText || !menuRef.current || !triggerRef.current) {
                return
            }

            const menuEl = menuRef.current
            const measured = menuEl.getBoundingClientRect().width
            const triggerRect = triggerRef.current.getBoundingClientRect()
            const viewportWidth = window.innerWidth

            let left = triggerRect.left
            if (left + measured > viewportWidth) {
                left = Math.max(MENU_GAP, viewportWidth - measured - MENU_GAP)
            }
            if (left < MENU_GAP) {
                left = MENU_GAP
            }

            setMenuPosition(prev => {
                if (!prev) {
                    return null
                }
                if (Math.abs(prev.left - left) < 0.5) {
                    return prev
                }

                return {
                    ...prev,
                    left,
                    triggerWidth: triggerRect.width,
                }
            })
        }, [isOpen, showText, options, menuMinWidth, menuPosition?.left, menuPosition?.top, menuPosition?.bottom])

        useEffect(() => {
            if (!isOpen) {
                return
            }

            optionsRefs.current[highlightedIndex]?.scrollIntoView({
                block: 'nearest',
            })
        }, [highlightedIndex, isOpen])

        useLayoutEffect(() => {
            if (!isOpen || disabled || isClosing || !menuPosition) {
                if (!isOpen) {
                    menuInitialFocusDoneRef.current = false
                }
                return
            }

            if (menuInitialFocusDoneRef.current) {
                return
            }

            menuInitialFocusDoneRef.current = true

            const firstEnabled = options.findIndex(o => !o.disabled)
            if (firstEnabled < 0) {
                return
            }

            let target = highlightedIndex
            if (!options[target] || options[target].disabled) {
                target = firstEnabled
            }

            requestAnimationFrame(() => {
                optionsRefs.current[target]?.focus()
            })
        }, [isOpen, isClosing, menuPosition, disabled, options, highlightedIndex])

        useEffect(() => {
            if (!isOpen || isClosing) {
                return
            }

            const handleTabTrap = (event: KeyboardEvent) => {
                if (event.key !== 'Tab') {
                    return
                }

                const menu = menuRef.current
                if (!menu) {
                    return
                }

                const focusables = Array.from(
                    menu.querySelectorAll<HTMLButtonElement>('button.menu-select-row:not(:disabled)')
                )

                if (focusables.length === 0) {
                    return
                }

                event.preventDefault()
                event.stopPropagation()

                const active = document.activeElement
                const idx = active instanceof HTMLElement ? focusables.indexOf(active as HTMLButtonElement) : -1

                if (idx === -1) {
                    focusables[0]?.focus()
                    const raw = focusables[0]?.getAttribute('data-option-index')
                    if (raw != null) {
                        const i = Number(raw)
                        if (!Number.isNaN(i)) {
                            setHighlightedIndex(i)
                        }
                    }
                    return
                }

                const nextIdx = event.shiftKey
                    ? idx <= 0
                        ? focusables.length - 1
                        : idx - 1
                    : idx >= focusables.length - 1
                      ? 0
                      : idx + 1

                const nextEl = focusables[nextIdx]
                nextEl?.focus()
                const raw = nextEl?.getAttribute('data-option-index')
                if (raw != null) {
                    const i = Number(raw)
                    if (!Number.isNaN(i)) {
                        setHighlightedIndex(i)
                    }
                }
            }

            document.addEventListener('keydown', handleTabTrap, true)

            return () => {
                document.removeEventListener('keydown', handleTabTrap, true)
            }
        }, [isOpen, isClosing, options.length])

        const isMenuExpanded = isOpen || isClosing

        const triggerClasses = cn(
            'select custom-select-trigger',
            {
                'select-sm': size === 'sm',
                'select-md': size === 'md',
                'select-lg': size === 'lg',
                'select-xl': size === 'xl',
                'select-neutral': emphasis === 'neutral',
                'select-negative': emphasis === 'negative',
                'select-positive': emphasis === 'positive',
                'select-warning': emphasis === 'warning',
                'select-disabled cursor-not-allowed': disabled,
                'custom-select-trigger--no-chevron': !showChevron,
                'custom-select-trigger--open': isMenuExpanded,
                'custom-select-trigger--icon-button-no-chevron': !showChevron,
                'cursor-not-allowed': disabled,
            },
            className
        )
        const labelClasses = cn('custom-select-label', className)

        const handleAssignRef = (node: HTMLSelectElement | null) => {
            nativeSelectRef.current = node
            if (typeof ref === 'function') {
                ref(node)
                return
            }
            if (ref) {
                const mutableRef = ref as React.MutableRefObject<HTMLSelectElement | null>
                mutableRef.current = node
            }
        }

        const handleSelectValue = (nextValue: string, nextIndex: number) => {
            if (!isControlled) {
                setInternalValue(nextValue)
            }

            setHighlightedIndex(nextIndex)
            handleClose()

            if (!nativeSelectRef.current) {
                return
            }

            nativeSelectRef.current.value = nextValue
            nativeSelectRef.current.dispatchEvent(new Event('change', { bubbles: true }))
            focusTrigger()
        }

        const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
            if (disabled || options.length === 0) {
                return
            }

            if (event.key === 'ArrowDown') {
                event.preventDefault()

                if (!isOpen) {
                    setIsOpen(true)
                    setHighlightedIndex(selectedIndex > -1 ? selectedIndex : 0)
                    return
                }

                setHighlightedIndex(prev => (prev + 1) % options.length)
                return
            }

            if (event.key === 'ArrowUp') {
                event.preventDefault()

                if (!isOpen) {
                    setIsOpen(true)
                    setHighlightedIndex(selectedIndex > -1 ? selectedIndex : options.length - 1)
                    return
                }

                setHighlightedIndex(prev => (prev - 1 + options.length) % options.length)
                return
            }

            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()

                if (!isOpen) {
                    setIsOpen(true)
                    return
                }

                const highlightedOption = options[highlightedIndex]
                if (!highlightedOption || highlightedOption.disabled) {
                    return
                }

                handleSelectValue(normalizeValue(highlightedOption.value), highlightedIndex)
                return
            }

            if (event.key === 'Escape') {
                handleClose()
            }
        }

        const shouldRenderPlaceholder = Boolean(placeholder) && !currentValue
        const triggerContent = shouldRenderPlaceholder
            ? placeholder
            : (selectedOption?.renderValue ?? selectedOption?.children ?? currentValue)
        const triggerLeftSlot = shouldRenderPlaceholder ? null : selectedOption?.leftSlot

        const selectedOptionLabel =
            typeof selectedOption?.children === 'string' || typeof selectedOption?.children === 'number'
                ? String(selectedOption.children)
                : String(selectedOption?.value ?? currentValue)

        const triggerAriaLabel =
            ariaLabel ?? (shouldRenderPlaceholder ? placeholder : selectedOptionLabel) ?? 'Select option'

        const nativeSelectProps = {
            ...restProps,
            disabled,
            onChange,
            multiple,
            ...(isControlled ? { value: currentValue } : { value: internalValue }),
        }

        if (multiple) {
            return (
                <label className={labelClasses}>
                    {beforeIcon && <Icon name={beforeIcon} />}
                    <select ref={handleAssignRef} className={triggerClasses} {...nativeSelectProps}>
                        {placeholder && (
                            <SelectOption disabled hidden value=''>
                                {placeholder}
                            </SelectOption>
                        )}
                        {options.map(option => (
                            <option
                                key={normalizeValue(option.value)}
                                value={normalizeValue(option.value)}
                                disabled={option.disabled}
                            >
                                {option.children}
                            </option>
                        ))}
                    </select>
                    {showChevron && <Icon name='arrow-down' className='custom-select-chevron' />}
                </label>
            )
        }

        return (
            <label ref={wrapperRef} className={cn(labelClasses)}>
                {beforeIcon && <Icon name={beforeIcon} />}
                <select
                    ref={handleAssignRef}
                    className='custom-select-hidden-native'
                    tabIndex={-1}
                    aria-hidden='true'
                    {...nativeSelectProps}
                >
                    {placeholder && (
                        <SelectOption disabled hidden value=''>
                            {placeholder}
                        </SelectOption>
                    )}
                    {options.map(option => (
                        <option
                            key={normalizeValue(option.value)}
                            value={normalizeValue(option.value)}
                            disabled={option.disabled}
                        >
                            {option.children}
                        </option>
                    ))}
                </select>
                <Button
                    ref={triggerRef}
                    type='button'
                    size={size}
                    emphasis={emphasis}
                    variant={buttonVariant}
                    active={active}
                    disabled={disabled}
                    tabIndex={isOpen && !isClosing ? -1 : undefined}
                    className={triggerClasses}
                    onClick={() => {
                        setIsOpen(prev => !prev)
                    }}
                    onKeyDown={handleTriggerKeyDown}
                    aria-haspopup='listbox'
                    aria-expanded={isOpen}
                    aria-controls={listboxId}
                    aria-label={triggerAriaLabel}
                >
                    {icon}
                    {triggerLeftSlot}
                    {showText && (
                        <span
                            className={cn('custom-select-value', {
                                'custom-select-placeholder': shouldRenderPlaceholder,
                            })}
                        >
                            {triggerContent}
                        </span>
                    )}
                    {showChevron && (
                        <Icon
                            name='arrow-down'
                            size={size}
                            className={cn('custom-select-chevron', {
                                'custom-select-chevron-open': isOpen,
                            })}
                        />
                    )}
                </Button>

                {(isOpen || isClosing) && !disabled && menuPosition && (
                    <Portal container={portalContainer}>
                        <Menu
                            id={listboxId}
                            ref={menuRef}
                            variant='selectList'
                            role='listbox'
                            className={cn('custom-select-menu', {
                                'custom-select-menu-closing pointer-events-none': isClosing,
                            })}
                            style={{
                                position: 'fixed',
                                ...(menuPosition.top !== undefined && { top: menuPosition.top }),
                                ...(menuPosition.bottom !== undefined && { bottom: menuPosition.bottom }),
                                left: menuPosition.left,
                                ...(showText
                                    ? { width: menuPosition.width }
                                    : {
                                          width: 'max-content',
                                          minWidth: `${Math.max(menuPosition.triggerWidth, menuMinWidth ?? 240)}px`,
                                      }),
                                ...(menuPosition.maxHeight && { maxHeight: menuPosition.maxHeight }),
                                ...(menuPosition.transformOrigin && {
                                    transformOrigin: menuPosition.transformOrigin,
                                }),
                            }}
                        >
                            {options.map((option, index) => {
                                const optionValue = normalizeValue(option.value)
                                const isSelected = optionValue === currentValue

                                return (
                                    <MenuItem key={optionValue} disabled={option.disabled} active={isSelected}>
                                        <MenuSelectRow
                                            ref={el => {
                                                optionsRefs.current[index] = el
                                            }}
                                            data-option-index={index}
                                            listboxOption
                                            role='option'
                                            aria-selected={isSelected}
                                            leftSlot={option.leftSlot}
                                            rightSlot={option.rightSlot}
                                            data-highlighted={
                                                highlightedIndex === index && !isSelected ? true : undefined
                                            }
                                            onMouseEnter={() => setHighlightedIndex(index)}
                                            onClick={() => {
                                                if (option.disabled) {
                                                    return
                                                }
                                                handleSelectValue(optionValue, index)
                                            }}
                                        >
                                            {option.renderContent ?? option.children}
                                        </MenuSelectRow>
                                    </MenuItem>
                                )
                            })}
                        </Menu>
                    </Portal>
                )}
            </label>
        )
    }
)

Select.displayName = 'Select'

export const SelectOption = ({
    children,
    renderContent: _renderContent,
    renderValue: _renderValue,
    leftSlot: _leftSlot,
    rightSlot: _rightSlot,
    ...props
}: SelectOptionType) => {
    return <option {...props}>{children}</option>
}
